var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import React, { useState } from "react";
import { get, isNil, isArray, flatten } from "lodash-es";
import { observer } from "mobx-react";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";
import { Heading, Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, FormControl, FormLabel, Stack, IconButton, theme, ThemeProvider, CSSReset } from "@chakra-ui/core";
import { CHART_TYPES } from "../core/common";
import { adjustDuplicates } from "../core/utils";

var generateSelectOptions = function generateSelectOptions(store) {
  return [].concat(store.formulas.filter(function (f) {
    return isArray(f.parsed.result);
  }).map(function (_ref) {
    var id = _ref.id,
        name = _ref.name;
    return React.createElement(
      "option",
      { key: id, value: JSON.stringify([id]) },
      name
    );
  }), flatten(store.tables.filter(function (t) {
    return t.columns.length > 0;
  }).map(function (t) {
    return t.columns.map(function (_ref2) {
      var id = _ref2.id,
          name = _ref2.name;
      return React.createElement(
        "option",
        { key: id, value: JSON.stringify([t.id, id]) },
        t.name,
        "[",
        name,
        "]"
      );
    });
  })));
};

// ToDo: error handling (whole file)
var ChartEditor = observer(function (_ref3) {
  var chart = _ref3.chart;

  var _useState = useState(get(chart, "chartType") || undefined),
      chartType = _useState[0],
      setChartType = _useState[1];

  var _useState2 = useState(get(chart, "dataRefs.xAxis") || undefined),
      xAxis = _useState2[0],
      setXAxis = _useState2[1];

  var _useState3 = useState(get(chart, "dataRefs.series") || [undefined]),
      series = _useState3[0],
      setSeries = _useState3[1];

  var isMenuOpen = chart.isMenuOpen,
      toggleMenuOpen = chart.toggleMenuOpen;

  var cleanSeries = series.filter(function (s) {
    return !isNil(s) && s !== "";
  });
  var submitDisabled = !chartType || cleanSeries.length === 0;

  var handleXAxisSelect = function handleXAxisSelect(colId) {
    setXAxis(colId);
    if (series.some(function (s) {
      return s === colId;
    })) {
      setSeries(series.map(function (s) {
        return s === colId ? undefined : s;
      }));
    }
  };

  var updateSerie = function updateSerie(serieIndex, serie) {
    setSeries(series.map(function (s, i) {
      return i === serieIndex ? serie : s;
    }));
  };

  var addSerie = function addSerie() {
    setSeries([].concat(series, [undefined]));
  };

  var deleteSerie = function deleteSerie(index) {
    setSeries(series.filter(function (s, i) {
      return i !== index;
    }));
  };

  var handleSubmit = function handleSubmit() {
    chart.setProps({
      chartType: chartType,
      dataRefs: {
        xAxis: xAxis && (typeof xAxis === "string" ? JSON.parse(xAxis) : xAxis),
        series: cleanSeries.map(function (s) {
          return typeof s === "string" ? JSON.parse(s) : s;
        })
      }
    });
    toggleMenuOpen(false);
  };

  return React.createElement(
    Modal,
    {
      isOpen: isMenuOpen,
      onClose: function onClose() {
        return toggleMenuOpen(false);
      },
      scrollBehavior: "outside"
    },
    React.createElement(ModalOverlay, null),
    React.createElement(
      ModalContent,
      null,
      React.createElement(
        ModalHeader,
        null,
        "Chart editor"
      ),
      React.createElement(ModalCloseButton, null),
      React.createElement(
        ModalBody,
        null,
        React.createElement(
          Stack,
          { spacing: 4 },
          React.createElement(
            FormControl,
            null,
            React.createElement(
              FormLabel,
              { htmlFor: "chart-type" },
              "Chart type:"
            ),
            React.createElement(
              Select,
              {
                id: "chart-type",
                placeholder: "Select chart type",
                value: chartType,
                onChange: function onChange(_ref4) {
                  var target = _ref4.target;
                  return setChartType(target.value);
                }
              },
              Object.values(CHART_TYPES).map(function (type, i) {
                return React.createElement(
                  "option",
                  { key: i, value: type },
                  type
                );
              })
            )
          ),
          React.createElement(
            FormControl,
            null,
            React.createElement(
              FormLabel,
              { htmlFor: "x-axis" },
              "X-axis:"
            ),
            React.createElement(
              Select,
              {
                id: "x-axis",
                placeholder: "Select column",
                value: xAxis,
                onChange: function onChange(_ref5) {
                  var target = _ref5.target;
                  return handleXAxisSelect(target.value);
                }
              },
              generateSelectOptions(chart.store)
            )
          ),
          React.createElement(
            FormControl,
            null,
            React.createElement(
              FormLabel,
              null,
              "Series:"
            ),
            React.createElement(
              Stack,
              { spacing: 2 },
              series.map(function (serie, i) {
                return React.createElement(
                  Box,
                  { key: i, display: "flex" },
                  React.createElement(
                    Select,
                    {
                      placeholder: "Select column",
                      value: serie,
                      onChange: function onChange(_ref6) {
                        var target = _ref6.target;
                        return updateSerie(i, target.value);
                      }
                    },
                    generateSelectOptions(chart.store)
                  ),
                  React.createElement(IconButton, {
                    ml: 2,
                    variant: "ghost",
                    icon: "delete",
                    disabled: series.length === 1,
                    onClick: function onClick() {
                      return deleteSerie(i);
                    }
                  })
                );
              })
            ),
            React.createElement(
              Button,
              {
                variant: "outline",
                leftIcon: "add",
                size: "xs",
                my: 2,
                onClick: addSerie
              },
              "add series"
            )
          )
        )
      ),
      React.createElement(
        ModalFooter,
        null,
        React.createElement(
          Button,
          {
            disabled: submitDisabled,
            variantColor: "blue",
            onClick: handleSubmit
          },
          "OK"
        )
      )
    )
  );
});

function Warning(props) {
  return React.createElement(Heading, _extends({ size: "sm", mx: 2, mt: 1 }, props));
}

var commonProperties = {
  width: 600,
  height: 300,
  margin: { top: 20, right: 20, bottom: 60, left: 80 },
  animate: false,
  isInteractive: false,
  enableSlices: false,
  enablePoints: false,
  curve: "monotoneX"
};

var getMinMax = function getMinMax(data, prop) {
  var multiplier = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
  return [Math.min, Math.max].map(function (fn) {
    return fn.apply(undefined, data.map(function (d) {
      return fn.apply(undefined, d.data.map(function (val) {
        return val[prop] || 0;
      }));
    })) * multiplier;
  });
};

var Chart = observer(function (_ref7) {
  var chart = _ref7.chart;

  if (!chart.data) {
    return React.createElement(
      Warning,
      null,
      "No chart data."
    );
  }

  var chartType = chart.chartType,
      _chart$data = chart.data,
      xAxis = _chart$data.xAxis,
      _chart$data$series = _chart$data.series,
      series = _chart$data$series === undefined ? [] : _chart$data$series;


  if (!chartType || xAxis.data.some(isNil) || series.some(function (s) {
    return s.data.some(isNil);
  })) {
    return React.createElement(
      Warning,
      null,
      "Wrong data provided."
    );
  }

  var content = null;
  var withFixedDuplicates = adjustDuplicates(series, "name");

  if (chartType === CHART_TYPES.line) {
    var data = withFixedDuplicates.map(function (s) {
      return {
        id: s.id, // ToDo: name?
        data: s.data.map(function (y, i) {
          return {
            x: xAxis.data[i],
            y: y
          };
        })
      };
    });

    var _getMinMax = getMinMax(data, "x"),
        xMin = _getMinMax[0],
        xMax = _getMinMax[1];

    var _getMinMax2 = getMinMax(data, "y", 1.25),
        yMin = _getMinMax2[0],
        yMax = _getMinMax2[1];

    content = React.createElement(ResponsiveLine, _extends({}, commonProperties, {
      data: data,
      xScale: {
        type: "linear",
        min: xMin,
        max: xMax
      },
      yScale: {
        type: "linear",
        min: yMin,
        max: yMax
      }
    }));
  }

  if (chartType === CHART_TYPES.bar) {
    var keys = withFixedDuplicates.map(function (s) {
      return "" + s.name;
    });
    var _data = xAxis.data.map(function (xValue, i) {
      return _extends({
        id: xValue
      }, withFixedDuplicates.reduce(function (acc, _ref8, j) {
        var _extends2;

        var name = _ref8.name,
            data = _ref8.data;
        return _extends({}, acc, (_extends2 = {}, _extends2[keys[j]] = data[i], _extends2));
      }, {}));
    });

    content = React.createElement(ResponsiveBar, {
      data: _data,
      keys: keys,
      margin: {
        top: 20,
        right: 40,
        bottom: 30,
        left: 60
      }
    });
  }

  return React.createElement(
    Box,
    { w: "700px", h: "100%", maxW: "700px", maxH: "300px" },
    content
  );
});

function ChartWrapper(_ref9) {
  var node = _ref9.node;
  var toggleMenuOpen = node.toggleMenuOpen;


  return React.createElement(
    ThemeProvider,
    { theme: theme },
    React.createElement(CSSReset, null),
    React.createElement(
      Box,
      { display: "flex" },
      React.createElement(
        Box,
        { borderWidth: "1px", py: 1 },
        React.createElement(
          IconButton,
          {
            variant: "outline",
            icon: "settings",
            size: "xs",
            mx: 2,
            onClick: function onClick() {
              return toggleMenuOpen(true);
            }
          },
          "menu"
        ),
        React.createElement(Chart, { chart: node }),
        React.createElement(ChartEditor, { chart: node })
      )
    )
  );
}

export default observer(ChartWrapper);